/**
 * Iter208.81 — Wizard: Preview Live Full-Screen di ciascun template.
 *
 * Modal che monta il template reale (Atrium / Casa Vicina / Villa Reale /
 * Meridian / Bottega Case) con i **dati veri** del pro loggato, così il pro
 * può navigare le 4 pagine + dettaglio annuncio in anteprima prima di scegliere.
 *
 * Isolato dal router principale via PreviewNavContext (evita nested Router,
 * proibito in react-router-dom v7) — i Link interni ai template navigano solo
 * nel modal, non nel browser.
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { X, ArrowSquareOut } from "@phosphor-icons/react";
import { PreviewNavContext } from "@/lib/previewNav";
import AtriumSite from "@/pages/public/AtriumSite";
import CasaVicinaSite from "@/pages/public/CasaVicinaSite";
import VillaRealeSite from "@/pages/public/VillaRealeSite";
import MeridianSite from "@/pages/public/MeridianSite";
import BottegaCaseSite from "@/pages/public/BottegaCaseSite";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TEMPLATE_COMPONENTS = {
  "atrium-editorial": AtriumSite,
  "casa-vicina":      CasaVicinaSite,
  "villa-reale":      VillaRealeSite,
  "meridian":         MeridianSite,
  "bottega-case":     BottegaCaseSite,
};

export default function TemplateLivePreviewModal({ templateId, templateLabel, slug, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // Iter208.81 — navigazione in-memory isolata dal router principale
  const [pathname, setPathname] = useState(`/site/${slug || ""}`);
  const navigate = useCallback((to) => {
    if (!to) return;
    // Supporta string o {pathname}
    const target = typeof to === "string" ? to : (to.pathname || "/");
    setPathname(target);
    // Reset scroll del contenuto quando cambia pagina
    const el = document.querySelector('[data-testid="wizard-live-preview-content"]');
    if (el) el.scrollTop = 0;
  }, []);

  useEffect(() => {
    if (!slug) { setLoading(false); setError("Utente non loggato."); return; }
    setLoading(true); setError("");
    axios.get(`${API}/public/site/${slug}`)
      .then((r) => {
        // Forziamo il template scelto per l'anteprima (bypassando quello salvato)
        setData({
          ...r.data,
          site_config: { ...(r.data.site_config || {}), template_id: templateId },
        });
      })
      .catch(() => setError("Impossibile caricare l'anteprima. Il tuo profilo pro non è ancora pubblicabile."))
      .finally(() => setLoading(false));
  }, [slug, templateId]);

  // ESC to close
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    // Blocca lo scroll dietro il modal
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  const TemplateCmp = TEMPLATE_COMPONENTS[templateId] || AtriumSite;

  return (
    <div
      className="fixed inset-0 z-[100] bg-slate-900/70 backdrop-blur-sm flex items-stretch justify-center"
      onClick={onClose}
      data-testid="wizard-live-preview-modal"
    >
      <div
        className="relative w-full h-full m-0 sm:m-6 sm:rounded-2xl bg-white overflow-hidden shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Toolbar */}
        <div className="flex items-center justify-between px-4 py-2 bg-slate-900 text-white shrink-0">
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-red-400"/>
            <span className="w-2.5 h-2.5 rounded-full bg-amber-400"/>
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-400"/>
            <span className="ml-3 text-sm font-medium" data-testid="wizard-live-preview-title">
              Anteprima live — <span className="text-purple-300">{templateLabel || templateId}</span>
            </span>
            <span className="ml-2 text-[11px] text-slate-400 hidden sm:inline">navigabile con i tuoi dati veri</span>
          </div>
          <div className="flex items-center gap-2">
            <a
              href={`/site/${slug}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-md bg-slate-800 hover:bg-slate-700 text-white transition-colors"
              data-testid="wizard-live-preview-open-new-tab"
            >
              <ArrowSquareOut size={12} weight="bold"/> Apri in nuova scheda
            </a>
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-md hover:bg-slate-800 transition-colors"
              aria-label="Chiudi"
              data-testid="wizard-live-preview-close"
            >
              <X size={16} weight="bold"/>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto bg-slate-50" data-testid="wizard-live-preview-content">
          {loading ? (
            <div className="p-16 text-center text-slate-500 text-sm">Caricamento anteprima…</div>
          ) : error ? (
            <div className="p-16 text-center text-slate-500 text-sm max-w-md mx-auto" data-testid="wizard-live-preview-error">
              {error}
            </div>
          ) : (
            <PreviewNavContext.Provider value={{ pathname, navigate }}>
              <TemplateCmp slug={slug} initialData={data}/>
            </PreviewNavContext.Provider>
          )}
        </div>
      </div>
    </div>
  );
}
